// Data Persistence Manager for saving, loading, and managing user data
export class DataPersistenceManager {
  static initialize() {
    this.storageKey = 'superannuation_calculator_data';
    this.compressionEnabled = true;
    this.autoSaveEnabled = true;
    this.autoSaveInterval = 30000; // 30 seconds
    this.autoSaveTimer = null;
    
    console.log('DataPersistenceManager initialized');
  }

  // Save data to localStorage with compression
  static saveData(data, key = null) {
    try {
      const dataKey = key || this.storageKey;
      const serializedData = JSON.stringify(data);
      
      // Simple compression for large data
      let compressedData = serializedData;
      if (this.compressionEnabled && serializedData.length > 1000) {
        compressedData = this.compress(serializedData);
      }
      
      localStorage.setItem(dataKey, compressedData);
      
      // Track save operation
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('data_saved', {
          dataSize: compressedData.length,
          compressed: this.compressionEnabled && serializedData.length > 1000
        });
      }
      
      return true;
    } catch (error) {
      console.error('Data save error:', error);
      return false;
    }
  }

  // Load data from localStorage with decompression
  static loadData(key = null) {
    try {
      const dataKey = key || this.storageKey;
      const compressedData = localStorage.getItem(dataKey);
      
      if (!compressedData) {
        return null;
      }
      
      // Try to decompress first, fall back to direct parsing
      let data;
      try {
        data = JSON.parse(this.decompress(compressedData));
      } catch {
        data = JSON.parse(compressedData);
      }
      
      return data;
    } catch (error) {
      console.error('Data load error:', error);
      return null;
    }
  }

  // Delete data from localStorage
  static deleteData(key = null) {
    try {
      const dataKey = key || this.storageKey;
      localStorage.removeItem(dataKey);
      
      // Track deletion
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('data_deleted');
      }
      
      return true;
    } catch (error) {
      console.error('Data deletion error:', error);
      return false;
    }
  }

  // Save calculator scenario
  static saveScenario(scenarioData, scenarioName) {
    const scenarios = this.loadScenarios() || {};
    scenarios[scenarioName] = {
      ...scenarioData,
      savedAt: new Date().toISOString(),
      name: scenarioName
    };
    
    this.saveData(scenarios, 'calculator_scenarios');
    
    // Track scenario save
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('scenario_saved', {
        scenarioName,
        calculatorType: scenarioData.calculatorType
      });
    }
    
    return true;
  }

  // Load calculator scenario
  static loadScenario(scenarioName) {
    const scenarios = this.loadScenarios();
    return scenarios ? scenarios[scenarioName] : null;
  }

  // Load all scenarios
  static loadScenarios() {
    return this.loadData('calculator_scenarios');
  }

  // Delete scenario
  static deleteScenario(scenarioName) {
    const scenarios = this.loadScenarios();
    if (scenarios && scenarios[scenarioName]) {
      delete scenarios[scenarioName];
      this.saveData(scenarios, 'calculator_scenarios');
      
      // Track scenario deletion
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('scenario_deleted', {
          scenarioName
        });
      }
      
      return true;
    }
    return false;
  }

  // Export data as JSON
  static exportData() {
    const allData = {
      scenarios: this.loadScenarios(),
      userData: this.loadData(),
      exportDate: new Date().toISOString(),
      version: '1.0.0'
    };
    
    const blob = new Blob([JSON.stringify(allData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `superannuation_calculator_data_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    // Track export
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('data_exported');
    }
  }

  // Import data from JSON file
  static importData(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          
          if (data.scenarios) {
            this.saveData(data.scenarios, 'calculator_scenarios');
          }
          
          if (data.userData) {
            this.saveData(data.userData);
          }
          
          // Track import
          if (window.AnalyticsManager) {
            window.AnalyticsManager.trackEvent('data_imported', {
              scenariosCount: data.scenarios ? Object.keys(data.scenarios).length : 0
            });
          }
          
          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('File read error'));
      reader.readAsText(file);
    });
  }

  // Start auto-save
  static startAutoSave(getCurrentData) {
    if (!this.autoSaveEnabled) return;
    
    this.stopAutoSave();
    
    this.autoSaveTimer = setInterval(() => {
      try {
        const currentData = getCurrentData();
        if (currentData) {
          this.saveData(currentData, 'auto_save');
        }
      } catch (error) {
        console.error('Auto-save error:', error);
      }
    }, this.autoSaveInterval);
  }

  // Stop auto-save
  static stopAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  // Simple compression using base64 and basic encoding
  static compress(data) {
    try {
      // Simple compression: remove whitespace and use base64
      const minified = data.replace(/\s+/g, '');
      return btoa(minified);
    } catch {
      return data;
    }
  }

  // Simple decompression
  static decompress(compressedData) {
    try {
      return atob(compressedData);
    } catch {
      return compressedData;
    }
  }

  // Get storage usage info
  static getStorageInfo() {
    const totalSize = JSON.stringify(localStorage).length;
    const scenarios = this.loadScenarios() || {};
    const scenariosSize = JSON.stringify(scenarios).length;
    
    return {
      totalSize,
      scenariosSize,
      scenariosCount: Object.keys(scenarios).length,
      compressionEnabled: this.compressionEnabled,
      autoSaveEnabled: this.autoSaveEnabled
    };
  }

  // Clear all data
  static clearAllData() {
    try {
      localStorage.clear();
      
      // Track data clearing
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('data_cleared');
      }
      
      return true;
    } catch (error) {
      console.error('Data clearing error:', error);
      return false;
    }
  }
}